import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import ConfirmDialog from "components/common/ConfirmDialog";
import { useAuth } from "hooks/useAuth";
import { useOrgAdminAccess } from "hooks/useOrgAdminAccess";
import {
  loadTciHappyPathProgress,
  loadTciPilotDemo,
  setTciHappyPathChecklistHidden,
} from "services/fde/tciHappyPathService";
import { db } from "services/firebase";

const TciSetupPage = () => {
  const [searchParams] = useSearchParams();
  const highlightStep = searchParams.get("step");
  const { userData, organizationId, isGlobalAdmin } = useAuth();
  const { canAccess } = useOrgAdminAccess();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    userData?.organizationId ||
    organizationId ||
    null;

  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [demoConfirmOpen, setDemoConfirmOpen] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [deferredFeedbackRows, setDeferredFeedbackRows] = useState([]);

  const refresh = useCallback(async () => {
    if (!institutionId) {
      setProgress(null);
      setDeferredFeedbackRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      const [next, deferredSnap] = await Promise.all([
        loadTciHappyPathProgress(institutionId),
        db
          .collection("consolidatedFeedback")
          .where("institutionId", "==", institutionId)
          .where("candidateDeferred", "==", true)
          .limit(10)
          .get(),
      ]);
      setProgress(next);
      setDeferredFeedbackRows(
        deferredSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      );
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load TCI setup progress.");
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!highlightStep || !progress?.steps?.length) return;
    const el = document.getElementById(`tci-setup-step-${highlightStep}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightStep, progress]);

  const percent = useMemo(() => {
    if (!progress?.totalCount) return 0;
    return Math.round((progress.completedCount / progress.totalCount) * 100);
  }, [progress]);

  const handleShowChecklist = async () => {
    try {
      await setTciHappyPathChecklistHidden(false);
      setSuccessMessage("Checklist will show again on the dashboard.");
    } catch (err) {
      setErrorMessage(err?.message || "Could not restore checklist.");
    }
  };

  const handleLoadDemo = async () => {
    setDemoLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await loadTciPilotDemo();
      setDemoConfirmOpen(false);
      setSuccessMessage("Pilot demo data loaded for this institution.");
      await refresh();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load pilot demo data.");
    } finally {
      setDemoLoading(false);
    }
  };

  if (!canAccess && !isGlobalAdmin) {
    return (
      <Alert severity="warning">
        TCI setup is available to organization admins. Ask your ChartMind Manager to complete
        institution setup.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 880, mx: "auto", p: 3 }}>
      <DashboardPageHeader
        title="TCI setup"
        subtitle="Walk the governance loop from institution setup through loop health — one sequence, existing screens."
      />

      {errorMessage ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      ) : null}
      {successMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      ) : null}

      {deferredFeedbackRows.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {deferredFeedbackRows.length} consolidated feedback item
          {deferredFeedbackRows.length === 1 ? "" : "s"} waiting for a TCI Manager before
          prompt candidates can be generated. Assign a TCI Manager to the covering clinical domain,
          then request candidate review again from session replay.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" gutterBottom>
              {loading
                ? "Loading progress…"
                : `${progress?.completedCount || 0} of ${progress?.totalCount || 9} complete`}
            </Typography>
            <LinearProgress
              variant={loading ? "indeterminate" : "determinate"}
              value={percent}
              sx={{ height: 8, borderRadius: 1 }}
            />
            {progress?.focusTciId ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Focus track: {progress.focusTciId}
                {progress.domainTciCount > 1
                  ? ` · ${progress.domainTciCount} TCIs on domain`
                  : ""}
              </Typography>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {userData?.hideTciHappyPathChecklist ? (
              <Button size="small" onClick={handleShowChecklist}>
                Show checklist
              </Button>
            ) : null}
            <LoadingButton
              size="small"
              variant="outlined"
              onClick={() => setDemoConfirmOpen(true)}
              loading={demoLoading}
            >
              Load pilot demo data
            </LoadingButton>
          </Stack>
        </Stack>
      </Paper>

      {progress?.allComplete ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Happy path complete. Open the{" "}
          <Button component={RouterLink} to="/dashboard/tci-manager" size="small">
            TCI Manager dashboard
          </Button>{" "}
          to run the live loop.
        </Alert>
      ) : null}

      <Paper variant="outlined">
        <List disablePadding>
          {(progress?.steps || []).map((step) => {
            const highlighted = highlightStep === step.id;
            return (
              <ListItem
                key={step.id}
                id={`tci-setup-step-${step.id}`}
                divider
                sx={{
                  alignItems: "flex-start",
                  bgcolor: highlighted ? "action.selected" : "transparent",
                }}
                secondaryAction={
                  <Button
                    component={RouterLink}
                    to={step.href}
                    size="small"
                    variant={step.done ? "text" : "contained"}
                  >
                    {step.done ? "Open" : "Continue"}
                  </Button>
                }
              >
                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                  {step.done ? (
                    <CheckCircleOutlineIcon color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon color="disabled" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle1">
                        {step.index}. {step.title}
                      </Typography>
                      <Chip
                        size="small"
                        label={step.done ? "Done" : "To do"}
                        color={step.done ? "success" : "default"}
                      />
                    </Stack>
                  }
                  secondary={step.description}
                />
              </ListItem>
            );
          })}
        </List>
      </Paper>

      <ConfirmDialog
        open={demoConfirmOpen}
        onClose={() => {
          if (!demoLoading) setDemoConfirmOpen(false);
        }}
        title="Load pilot demo data?"
        message="This merges demo loop data (domain TCIs, governance, sample sessions, annotations, and a pending candidate) into your institution. It does not delete prompts or unrelated org data."
        confirmLabel="Load demo data"
        confirmColor="primary"
        loading={demoLoading}
        onConfirm={handleLoadDemo}
      />
    </Box>
  );
};

export default TciSetupPage;
